import { ArrowDownUp, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { IntegrationSortBy, IntegrationSortOrder } from "../types";
import {
  ORDER_OPTIONS,
  SORT_BY_LABEL,
} from "../utils/integrations-list-params";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";

type IntegrationsSortMenuProps = {
  sortBy: IntegrationSortBy;
  order: IntegrationSortOrder;
  onSortByChange: (sortBy: IntegrationSortBy) => void;
  onOrderChange: (order: IntegrationSortOrder) => void;
};

/**
 * Combined sort control for the integrations list: a single dropdown with a "Sort by"
 * section (Name / Status) and a field-aware "Order" section. Each item keeps the menu
 * open on select (preventDefault) so users can adjust both axes in one interaction.
 * Selection state lives in the URL — this component is presentational.
 */
export function IntegrationsSortMenu({
  sortBy,
  order,
  onSortByChange,
  onOrderChange,
}: IntegrationsSortMenuProps) {
  const { t } = useTranslation();

  const renderCheck = (active: boolean) =>
    active ? (
      <Check className="size-4 text-primary" />
    ) : (
      <span className="size-4" aria-hidden />
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" >
          <div className="min-w-24 inline-flex justify-between items-center">
            <div className="flex gap-1 items-center">
              <ArrowDownUp />
              {t(SORT_BY_LABEL[sortBy])}
            </div>
            <ChevronDown className="size-4 opacity-60" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-50">
        <DropdownMenuLabel>{t("common.sortBy")}</DropdownMenuLabel>
        {(Object.keys(SORT_BY_LABEL) as IntegrationSortBy[]).map((value) => (
          <DropdownMenuItem
            key={value}
            onSelect={(e) => {
              e.preventDefault();
              onSortByChange(value);
            }}
          >
            {renderCheck(sortBy === value)}
            {t(SORT_BY_LABEL[value])}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuLabel>{t("common.order")}</DropdownMenuLabel>
        {ORDER_OPTIONS[sortBy].map((option) => (
          <DropdownMenuItem
            key={option.order}
            onSelect={(e) => {
              e.preventDefault();
              onOrderChange(option.order);
            }}
          >
            {renderCheck(order === option.order)}
            {t(option.label)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default IntegrationsSortMenu;
